import { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, MenuItem, Select,
  Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';

// Project-side roles (user_projects.role_id)
const PROJECT_ROLES = [
  { id: 1, name: 'project_lead',   label: 'Project Lead',   desc: 'Full access + team management', color: 'error' },
  { id: 2, name: 'project_member', label: 'Project Member', desc: 'All sites, no management',       color: 'primary' },
  { id: 3, name: 'site_member',    label: 'Site Member',    desc: 'Assigned sites only',            color: 'info' },
];
const ROLE_BY_NAME = Object.fromEntries(PROJECT_ROLES.map(r => [r.name, r]));

function ProjectRoleChip({ roleName }) {
  if (roleName === 'technical') return <Chip label="Technical Staff" size="small" variant="outlined" color="default" />;
  const r = ROLE_BY_NAME[roleName];
  if (!r) return <Chip label={roleName} size="small" />;
  return <Chip label={r.label} size="small" color={r.color} />;
}

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

// Site chips for a member — shows assigned sites or "All sites"
function SiteAccess({ member, projectId, projectSites, canManage, onChanged }) {
  const [addingSite, setAddingSite] = useState(false);
  const [newSiteId, setNewSiteId] = useState('');

  const assignedSiteIds = new Set((member.sites || []).map(s => s.id));
  const availableSites = projectSites.filter(s => !assignedSiteIds.has(s.id));

  const addSite = async () => {
    if (!newSiteId) return;
    await fetch(`/api/access/user-sites`, {
      method: 'POST', headers: bearerHeaders(),
      body: JSON.stringify({ userId: member.user_id, siteId: newSiteId }),
    });
    setAddingSite(false); setNewSiteId('');
    onChanged();
  };

  const removeSite = async (siteId) => {
    await fetch(`/api/access/user-sites/${member.user_id}/${siteId}`, {
      method: 'DELETE', headers: bearerHeaders(),
    });
    onChanged();
  };

  // Project Lead and Project Member see all sites — no per-site management
  if (member.project_role === 'project_lead' || member.project_role === 'project_member') {
    return <Typography variant="caption" color="text.secondary">All sites</Typography>;
  }

  // Technical staff — show their IoT scope
  if (member.team_type === 'iot') {
    return member.sites?.length
      ? <Stack direction="row" spacing={0.5} flexWrap="wrap">{member.sites.map(s => <Chip key={s.id} label={s.name} size="small" variant="outlined" />)}</Stack>
      : <Typography variant="caption" color="text.secondary">All sites (IoT)</Typography>;
  }

  // Site Member — show assigned sites with remove, and add button
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
      {(member.sites || []).map(s => (
        <Chip key={s.id} label={s.name} size="small" variant="outlined" color="info"
          onDelete={canManage ? () => removeSite(s.id) : undefined} />
      ))}
      {!member.sites?.length && <Typography variant="caption" color="text.disabled">No sites</Typography>}
      {canManage && !addingSite && availableSites.length > 0 && (
        <Chip label="+ site" size="small" variant="outlined" onClick={() => setAddingSite(true)}
          sx={{ cursor: 'pointer', borderStyle: 'dashed' }} />
      )}
      {addingSite && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Select size="small" value={newSiteId} onChange={e => setNewSiteId(e.target.value)}
            displayEmpty sx={{ minWidth: 120, fontSize: 12 }}>
            <MenuItem value=""><em>Site...</em></MenuItem>
            {availableSites.map(s => <MenuItem key={s.id} value={s.id} sx={{ fontSize: 12 }}>{s.name}</MenuItem>)}
          </Select>
          <Button size="small" onClick={addSite} disabled={!newSiteId}>Add</Button>
          <Button size="small" color="inherit" onClick={() => setAddingSite(false)}>✕</Button>
        </Stack>
      )}
    </Stack>
  );
}

export default function ProjectTeamTab({ projectId }) {
  const [members, setMembers] = useState([]);
  const [projectSites, setProjectSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [membersRes, sitesRes] = await Promise.all([
        fetch(`/api/access/projects/${projectId}/members`, { headers: bearerHeaders() }),
        fetch(`/api/access/projects/${projectId}/sites`, { headers: bearerHeaders() }),
      ]);
      setMembers(membersRes.ok ? (await membersRes.json()).members || [] : []);
      setProjectSites(sitesRes.ok ? (await sitesRes.json()).sites || [] : []);
    } catch { setMsg('Failed to load team data.'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const updateRole = async (userId, roleId) => {
    const res = await fetch(`/api/access/projects/${projectId}/members/${userId}`, {
      method: 'PUT', headers: bearerHeaders(),
      body: JSON.stringify({ roleId }),
    });
    if (res.ok) load();
  };

  const removeMember = async (userId) => {
    await fetch(`/api/access/projects/${projectId}/members/${userId}`, {
      method: 'DELETE', headers: bearerHeaders(),
    });
    load();
  };

  if (loading) return <CircularProgress sx={{ mt: 4 }} />;

  const projectTeam = members.filter(m => m.team_type === 'project');
  const technicalTeam = members.filter(m => m.team_type === 'iot');

  const canManage = true; // ProjectSettingsPage passes canManage, but we default to true here

  return (
    <Box>
      {msg && <Typography variant="body2" color="error.main" sx={{ mb: 2 }}>{msg}</Typography>}

      {/* Explanation + role reference */}
      <Box sx={{ mb: 2.5, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1.5 }}>Project team roles</Typography>
        <Stack spacing={0.5} sx={{ mb: 2 }}>
          {[
            <>To add a team member: go to <b>System Settings → Users &amp; Access</b>, set their system role to <b>User</b>, then click <b>+ project</b> next to their name.</>,
            <>Set the <b>Project Role</b> below to control what they can do inside this project.</>,
            <>For <b>Site Members</b>: assign specific sites using the <b>Site Access</b> column. They will only see those sites.</>,
          ].map((text, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
              <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 18, mt: '1px', color: 'text.primary' }}>{i + 1}.</Typography>
              <Typography variant="caption" color="text.secondary">{text}</Typography>
            </Stack>
          ))}
        </Stack>
        <Table size="small" sx={{ '& td, & th': { fontSize: 11, py: 0.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Project Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Site Access</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Can manage team?</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              { role: 'Project Lead',   access: 'All sites', manage: 'Yes — can change roles and assign sites' },
              { role: 'Project Member', access: 'All sites', manage: 'No' },
              { role: 'Site Member',    access: 'Assigned sites only', manage: 'No' },
            ].map(row => (
              <TableRow key={row.role}>
                <TableCell><b>{row.role}</b></TableCell>
                <TableCell>{row.access}</TableCell>
                <TableCell>{row.manage}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell sx={{ minWidth: 160 }}>Project Role</TableCell>
            <TableCell sx={{ minWidth: 220 }}>Site Access</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {projectTeam.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="caption" color="text.secondary">
                  No team members yet. Add them via System Settings → Users &amp; Access.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {projectTeam.map(m => (
            <TableRow key={m.user_id}>
              <TableCell sx={{ fontWeight: 500 }}>{m.user_nick_name || '\u2014'}</TableCell>
              <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{m.user_email}</TableCell>
              <TableCell>
                <Select size="small" value={m.project_role_id ?? 3}
                  onChange={e => updateRole(m.user_id, e.target.value)}
                  sx={{ minWidth: 140, fontSize: 12 }}>
                  {PROJECT_ROLES.map(r => (
                    <MenuItem key={r.id} value={r.id} sx={{ fontSize: 13 }}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell>
                <SiteAccess member={m} projectId={projectId} projectSites={projectSites}
                  canManage={canManage} onChanged={load} />
              </TableCell>
              <TableCell>
                <Button size="small" color="error" onClick={() => removeMember(m.user_id)}>Remove</Button>
              </TableCell>
            </TableRow>
          ))}

          {/* Technical staff (IoT-only) */}
          {technicalTeam.length > 0 && (
            <TableRow>
              <TableCell colSpan={5} sx={{ pt: 2, pb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Technical Staff (IoT access only)
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {technicalTeam.map(m => (
            <TableRow key={`iot-${m.user_id}`} sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 500 }}>{m.user_nick_name || '\u2014'}</TableCell>
              <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{m.user_email}</TableCell>
              <TableCell><ProjectRoleChip roleName="technical" /></TableCell>
              <TableCell>
                <SiteAccess member={m} projectId={projectId} projectSites={projectSites}
                  canManage={false} onChanged={load} />
              </TableCell>
              <TableCell />
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        To add team members: System Settings → Users &amp; Access → grant project access.
      </Typography>
    </Box>
  );
}
